// src/runtime/WorkflowRuntime/types.ts
// ============================================
// WorkflowRuntime - 类型定义（冻结版本 v1.0）
//
// 职责：定义工作流相关的所有类型
// 支持：Step / Condition / Loop / Retry / Parallel / Human Approval / Memory
//
// 冻结声明：此文件为 v1.0 冻结版本，新增类型只追加不修改
// ============================================

import type { RuntimeName } from '../../types/runtime';

// ============================================
// 工作流状态
// ============================================

export enum WorkflowStatus {
  /** 草稿（已定义未执行） */
  Draft = 'draft',
  /** 等待执行 */
  Pending = 'pending',
  /** 执行中 */
  Running = 'running',
  /** 已暂停（等待人工审批） */
  Paused = 'paused',
  /** 已完成 */
  Completed = 'completed',
  /** 执行失败 */
  Failed = 'failed',
  /** 已取消 */
  Canceled = 'canceled',
  /** 已超时 */
  Timeout = 'timeout',
}

// ============================================
// 步骤状态
// ============================================

export enum StepStatus {
  Pending = 'pending',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
  Skipped = 'skipped',
  WaitingApproval = 'waiting_approval',
  Canceled = 'canceled',
}

// ============================================
// 步骤类型枚举
// ============================================

export enum StepType {
  /** 任务步骤：调用 Runtime 执行操作 */
  Task = 'task',
  /** 条件步骤：if/else 分支 */
  Condition = 'condition',
  /** 循环步骤：遍历列表或范围 */
  Loop = 'loop',
  /** 并行步骤：多个分支同时执行 */
  Parallel = 'parallel',
  /** 人工审批步骤：暂停等待确认 */
  HumanApproval = 'human_approval',
  /** 延迟步骤：等待指定时间 */
  Delay = 'delay',
  /** 赋值步骤：设置上下文变量 */
  Assign = 'assign',
  /** 日志步骤：记录信息 */
  Log = 'log',
}

// ============================================
// 重试策略
// ============================================

export interface RetryPolicy {
  /** 最大重试次数（默认 0=不重试） */
  maxRetries: number;
  /** 重试间隔（毫秒，默认 1000） */
  interval: number;
  /** 指数退避倍数（默认 2） */
  backoffMultiplier: number;
  /** 最大间隔（毫秒，默认 30000） */
  maxInterval: number;
  /** 可重试的错误类型（正则匹配错误消息） */
  retryableErrors?: string[];
}

export const DefaultRetryPolicy: RetryPolicy = {
  maxRetries: 0,
  interval: 1000,
  backoffMultiplier: 2,
  maxInterval: 30000,
};

// ============================================
// 步骤基础接口
// ============================================

export interface StepBase {
  /** 步骤唯一 ID */
  id: string;
  /** 步骤名称 */
  name: string;
  /** 步骤类型 */
  type: StepType;
  /** 步骤描述 */
  description?: string;
  /** 重试策略 */
  retry?: RetryPolicy;
  /** 超时（毫秒） */
  timeout?: number;
  /** 条件：满足时才执行此步骤（表达式字符串或函数） */
  when?: string;
  /** P2-6：是否启用缓存（命中缓存时不调用 Runtime，直接返回缓存结果） */
  cache?: boolean;
  /** Sprint 1 Task 3：引用的 SOP ID（用于读取 SOP 步骤作为执行上下文） */
  sop?: string;
}

// ============================================
// Sprint 1 Task 3：SOP 引用（工作流关联 SOP）
// ============================================

/**
 * SOP 步骤引用：用于将 SOP 的某个步骤内容注入到 Workflow 步骤的上下文中
 * - sopId：对应真实 Vault D:/Obsidian/小舍AI_中央大脑库/XEAOS/SOP/ 目录下的 SOP ID
 * - stepIndex：可选，指定 SOP 的第几步（不指定则引用整个 SOP）
 */
export interface SOPStepRef {
  /** SOP 唯一 ID */
  sopId: string;
  /** 引用的步骤索引（0-based，不指定则引用整个 SOP） */
  stepIndex?: number;
}

// ============================================
// 任务步骤：调用 Runtime
// ============================================

export interface TaskStep extends StepBase {
  type: StepType.Task;
  /** 目标 Runtime 名称 */
  runtime: RuntimeName;
  /** 执行的命令 */
  command: string;
  /** 静态参数 */
  params?: Record<string, unknown>;
  /** 输入映射：从上下文变量映射到参数 */
  inputMapping?: Record<string, string>;
  /** 输出键：将结果存储到上下文的哪个键 */
  outputKey?: string;
}

// ============================================
// 条件步骤：if/else
// ============================================

export interface ConditionStep extends StepBase {
  type: StepType.Condition;
  /** 条件表达式（字符串形式，引用上下文变量） */
  condition: string;
  /** 满足条件时执行的步骤 */
  ifSteps: Step[];
  /** 不满足条件时执行的步骤 */
  elseSteps?: Step[];
}

// ============================================
// 循环步骤：遍历列表
// ============================================

export interface LoopStep extends StepBase {
  type: StepType.Loop;
  /** 循环项来源：上下文变量路径（如 'results.items'） */
  items: string;
  /** 循环变量名（存入上下文） */
  variable: string;
  /** 循环体步骤 */
  body: Step[];
  /** 是否并行执行所有迭代 */
  parallel?: boolean;
  /** 最大并行度（parallel=true 时生效） */
  concurrency?: number;
}

// ============================================
// 并行步骤：多个分支同时执行
// ============================================

export interface ParallelStep extends StepBase {
  type: StepType.Parallel;
  /** 并行分支（每个分支是一组步骤） */
  branches: Step[][];
  /** 是否等待所有分支完成（true=all, false=any） */
  waitForAll?: boolean;
}

// ============================================
// 人工审批步骤
// ============================================

export interface HumanApprovalStep extends StepBase {
  type: StepType.HumanApproval;
  /** 审批提示消息 */
  message: string;
  /** 审批人列表（预留） */
  approvers?: string[];
  /** 超时自动拒绝（毫秒，0=不超时） */
  timeout?: number;
  /** 审批上下文数据（展示给审批人的信息） */
  contextData?: Record<string, unknown>;
}

// ============================================
// 延迟步骤
// ============================================

export interface DelayStep extends StepBase {
  type: StepType.Delay;
  /** 延迟时长（毫秒） */
  duration: number;
}

// ============================================
// 赋值步骤
// ============================================

export interface AssignStep extends StepBase {
  type: StepType.Assign;
  /** 赋值目标（上下文变量路径） */
  target: string;
  /** 赋值内容（静态值或表达式） */
  value: unknown;
}

// ============================================
// 日志步骤
// ============================================

export interface LogStep extends StepBase {
  type: StepType.Log;
  /** 日志级别 */
  level: 'debug' | 'info' | 'warn' | 'error';
  /** 日志消息 */
  message: string;
}

// ============================================
// 步骤联合类型
// ============================================

export type Step =
  | TaskStep
  | ConditionStep
  | LoopStep
  | ParallelStep
  | HumanApprovalStep
  | DelayStep
  | AssignStep
  | LogStep;

// ============================================
// 工作流定义
// ============================================

export interface Workflow {
  /** 工作流唯一 ID */
  id: string;
  /** 工作流名称 */
  name: string;
  /** 工作流描述 */
  description?: string;
  /** 版本 */
  version?: string;
  /** 工作流步骤 */
  steps: Step[];
  /** 输入参数定义 */
  inputs?: Record<string, unknown>;
  /** 输出参数定义 */
  outputs?: string[];
  /** 默认重试策略 */
  defaultRetry?: RetryPolicy;
  /** 全局超时（毫秒） */
  timeout?: number;
  /** 标签 */
  tags?: string[];
  /** 创建时间 */
  createdAt?: string;
  /** 更新时间 */
  updatedAt?: string;
  /** Sprint 1 Task 3：工作流引用的 SOP ID 列表（用于执行前加载 SOP 上下文） */
  sopRefs?: string[];
}

// ============================================
// 工作流执行实例
// ============================================

export interface WorkflowExecution {
  /** 执行实例 ID */
  executionId: string;
  /** 工作流 ID */
  workflowId: string;
  /** 工作流名称 */
  workflowName: string;
  /** 执行状态 */
  status: WorkflowStatus;
  /** 执行上下文（变量存储） */
  context: WorkflowContext;
  /** 步骤执行结果 */
  stepResults: StepResult[];
  /** 当前执行的步骤索引 */
  currentStepIndex: number;
  /** 开始时间 */
  startedAt: string;
  /** 结束时间 */
  finishedAt?: string;
  /** 错误信息 */
  error?: string;
  /** 触发来源 */
  triggeredBy?: string;
}

// ============================================
// 工作流上下文
// ============================================

export interface WorkflowContext {
  /** 输入参数 */
  inputs: Record<string, unknown>;
  /** 步骤输出（按 outputKey 存储） */
  outputs: Record<string, unknown>;
  /** 临时变量 */
  variables: Record<string, unknown>;
}

// ============================================
// 步骤执行结果
// ============================================

export interface StepResult {
  /** 步骤 ID */
  stepId: string;
  /** 步骤名称 */
  stepName: string;
  /** 步骤类型 */
  stepType: StepType;
  /** 执行状态 */
  status: StepStatus;
  /** 输出结果 */
  output?: unknown;
  /** 错误信息 */
  error?: string;
  /** 开始时间 */
  startedAt: string;
  /** 结束时间 */
  finishedAt?: string;
  /** 执行耗时（毫秒） */
  duration?: number;
  /** 重试次数 */
  retryCount?: number;
}

// ============================================
// 查询/命令枚举
// ============================================

export enum WorkflowQuery {
  /** 列出所有工作流 */
  List = 'list',
  /** 获取工作流定义 */
  Get = 'get',
  /** 获取执行实例 */
  GetExecution = 'get_execution',
  /** 列出执行历史 */
  ListExecutions = 'list_executions',
  /** 获取执行状态 */
  GetStatus = 'get_status',
  // ----- 监控查询（P1-3 新增） -----
  /** 获取执行指标 */
  GetMetrics = 'get_metrics',
  /** 获取聚合指标 */
  GetAggregateMetrics = 'get_aggregate_metrics',
  /** 获取执行日志 */
  GetLogs = 'get_logs',
  /** 获取最后 n 条日志 */
  TailLogs = 'tail_logs',
  /** 获取健康度评分 */
  GetHealthScore = 'get_health_score',
  // ----- 版本查询（P1-4 新增） -----
  /** 列出工作流版本 */
  ListVersions = 'list_versions',
  /** 获取指定版本详情 */
  GetVersion = 'get_version',
  /** 对比两个版本差异 */
  DiffVersions = 'diff_versions',
  // ----- P2：模板实例化 -----
  /** 从模板库一键创建可运行的工作流实例 */
  InstantiateTemplate = 'instantiate_template',
  // ----- 调试查询（P1-5 新增） -----
  /** 获取调试状态 */
  GetDebugState = 'get_debug_state',
  /** 列出断点 */
  ListBreakpoints = 'list_breakpoints',
  /** 检查变量 */
  InspectVariable = 'inspect_variable',
  /** 求值表达式 */
  EvaluateExpression = 'evaluate_expression',
  // ----- 自优化查询（P2-6 新增） -----
  /** 获取优化报告（含统计、卡点、建议） */
  GetOptimizationReport = 'get_optimization_report',
}

export enum WorkflowCommand {
  /** 创建工作流 */
  Create = 'create',
  /** 更新工作流 */
  Update = 'update',
  /** 删除工作流 */
  Delete = 'delete',
  /** 执行工作流 */
  Run = 'run',
  /** 暂停执行（等待人工审批） */
  Pause = 'pause',
  /** 恢复执行（审批通过后） */
  Resume = 'resume',
  /** 取消执行 */
  Cancel = 'cancel',
  /** 重试失败步骤 */
  Retry = 'retry',
  // ----- DSL 命令（v1.1 新增，仅追加不修改） -----
  /** 从文件/目录加载 DSL 工作流（YAML/JSON） */
  LoadDSL = 'load_dsl',
  /** 从字符串解析 DSL（不注册） */
  ParseDSL = 'parse_dsl',
  /** 从字符串加载并注册 DSL 工作流 */
  LoadStringDSL = 'load_string_dsl',
  // ----- Marketplace 命令（Epic 3 新增） -----
  /** 列出 Marketplace 中所有工作流（含元数据） */
  MarketplaceList = 'marketplace_list',
  /** 获取 Marketplace 中指定工作流的详细信息 */
  MarketplaceGet = 'marketplace_get',
  /** 按关键词搜索 Marketplace 工作流 */
  MarketplaceSearch = 'marketplace_search',
  /** 重新扫描 workflows/ 目录并加载所有 DSL 文件 */
  MarketplaceReload = 'marketplace_reload',
  // ----- 版本命令（P1-4 新增） -----
  /** 回滚到指定版本 */
  RollbackVersion = 'rollback_version',
  /** 发布灰度版本 */
  PublishCanary = 'publish_canary',
  /** 为版本打标签 */
  TagVersion = 'tag_version',
  // ----- 调试命令（P1-5 新增） -----
  /** 设置断点 */
  SetBreakpoint = 'set_breakpoint',
  /** 移除断点 */
  RemoveBreakpoint = 'remove_breakpoint',
  /** 单步执行 */
  StepNext = 'step_next',
  /** 继续执行（从断点继续） */
  DebugContinue = 'debug_continue',
  /** 设置变量 */
  SetVariable = 'set_variable',
  /** 暂停调试 */
  PauseDebug = 'pause_debug',
  // ----- 自优化命令（P2-6 新增） -----
  /** 优化工作流定义（生成优化后的步骤） */
  OptimizeWorkflow = 'optimize_workflow',
  /** 获取工作流执行分析（步骤统计） */
  GetAnalytics = 'get_analytics',
  /** 获取缓存统计（命中率/大小） */
  GetCacheStats = 'get_cache_stats',
  /** 失效缓存（指定工作流或步骤） */
  InvalidateCache = 'invalidate_cache',
  /** 获取优化建议列表 */
  GetSuggestions = 'get_suggestions',
}

// ============================================
// 审批结果
// ============================================

export interface ApprovalResult {
  /** 是否批准 */
  approved: boolean;
  /** 审批意见 */
  comment?: string;
  /** 审批人 */
  approver?: string;
}
